(ns frontend.ui.component.lp-portal.report.component.vertical-table.view
  (:require
   [common.date-formats :as date]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.ui.component.lp-portal.voima-components.card.view :as card]
   [frontend.ui.component.lp-portal.report.component.horizontal-table.style :as style]
   [frontend.ui.component.lp-portal.voima-components.text-content.view :as text-content]
   [frontend.ui.page.lp-portal.report.util :as report-utils]
   [frontend.ui.style.theme.core :as theme]
   [frontend.utils :as fu]
   [herb.core :as h]))


(defn heading-row-spanned
  [heading]
  (into [:tr
         {:class (h/<class style/heading-tr)}]
        (->> heading
             :cols
             (keep (fn [{:keys [span label]}]
                     (when-not (zero? span)
                       [:th
                        (merge {:class (h/<class style/th-spanned label)}
                               (when (pos? span)
                                 {:col-span span}))
                        (or label "")]))))))

(defn heading-row-heading
  [heading]
  (into [:tr
         {:class (h/<class style/heading-tr)}]
        (->> heading
             :cols
             (map-indexed (fn [idx lbl]
                            [:th
                             {:class (h/<class (partial style/th-heading lbl idx))}
                             (or lbl "")]))
             (vec))))

(defn heading-row-info
  [heading]
  (into [:tr
         {:class (h/<class style/heading-tr)}]
        (->> heading
             :cols
             (map-indexed (fn [idx lbl]
                     [:th
                      {:class (h/<class (partial style/th-info lbl idx))}
                      (or lbl "")]))
             (vec))))

(defn format-data
  [col-data-type col-decimals col-multipliers col-date-format col-empty-indicator row-style data]
  (let [col-decimals-set? (not (cu/blank-value? col-decimals))
        data-available? (not (cu/blank-value? data))
        date-format-set? (not (cu/blank-value? col-date-format))
        is-data-row? (or (cu/blank-value? row-style) (= row-style "note"))
        multiplier (if (not (cu/blank-value? col-multipliers))
                     col-multipliers
                     1)
        decimal-amount (if col-decimals-set?
                         col-decimals
                         (case col-data-type
                           :table-data-type/currency 1
                           :table-data-type/number 2
                           :table-data-type/percentage 0
                           :table-data-type/multiple 1
                           nil))]
    (if data-available?
      (case col-data-type
        :table-data-type/date
        (if date-format-set?
          (date/format-millis data col-date-format nil)
          (date/format-millis data "dd.MM.yyyy" nil))
        :table-data-type/currency
        ;;TODO do we want the option to include suffix??
        (fu/number-string->thousand-separators
         (report-utils/->float-x-decimals (/ data multiplier) decimal-amount)
         ","
         ".")
        :table-data-type/multiple
        ;;TODO should we give options for the multiplier symbol?
        (report-utils/->float-x-decimals "x" data decimal-amount)
        :table-data-type/number
        (report-utils/->float-x-decimals data decimal-amount)
        :table-data-type/percentage
        (fu/format-percentage-with-decimals data decimal-amount)
        :table-data-type/text
        (str data))
      (or (and is-data-row? col-empty-indicator) nil))))


(defn data-row
  [{:keys [alignment-row highlight-row
           column-font-style-row column-style-row
           table-style]}
   row idx even-row-background-color odd-row-background-color sub-heading-color
   sub-total-color total-color empty-line-color icons-row props]
  (let [{:keys [row-data row-highlight row-type row-font-style row-multipliers row-data-types row-decimals format-row
                empty-indicator-row row-style]}
        row]

    (into
     ^{:key (gensym "key-")}
     [:tr
      {:class (h/<class style/data-row-vertical-table
                        row-type
                        row-highlight
                        row-font-style
                        idx
                        even-row-background-color
                        odd-row-background-color
                        sub-heading-color
                        sub-total-color
                        total-color
                        empty-line-color
                        table-style
                        row-style
                        props)}
      (->> row-data
           (map-indexed
            (fn [idx data]
              (let [column-style (nth column-style-row idx nil)
                    column-alignment (nth alignment-row idx nil)
                    column-highlight (nth highlight-row idx nil)
                    column-text-style (nth column-font-style-row idx nil)
                    icon-map (nth icons-row idx nil)]
                ^{:key (gensym "key-")}
                [:td
                 {:class (h/<class style/data-row-td
                                   column-alignment
                                   row-data-types
                                   column-highlight
                                   column-text-style
                                   column-style
                                   idx
                                   props)}
                 [:div {:class "inline-flex items-center gap-1"}
                  [:span
                   (if-not (= idx 0)
                     (format-data row-data-types
                                  row-decimals
                                  row-multipliers
                                  format-row
                                  empty-indicator-row
                                  row-type
                                  data)
                     (str data))]
                  (when-let [icon (:icon icon-map)]
                    [:span {:style {:font-family "FontAwesome"
                                    :color (or (:color icon-map) "#000000")}
                            :class "text-base ml-1"}
                     icon])]])))
           (doall))])))

(defn data-row-stacked
  [{:keys [_headers
           column-font-style-row
           _heading-count _rows _table-row-alignment]} row text-color]
  (let [{:keys [row-data row-highlight row-type row-font-style row-multipliers
                row-data-types row-decimals format-row empty-indicator-row]} row
        data-heading (first row-data)
        data-value (format-data row-data-types
                                row-decimals
                                row-multipliers
                                format-row
                                empty-indicator-row
                                row-type
                                (second row-data))
        color-style (when text-color {:style {:color text-color}})]
    [:<>
     [:tr {:class (h/<class style/data-row-block-table-stacked row-type row-highlight row-font-style)}
      [:td (merge {:class (h/<class style/data-row-td-heading-stacked (first column-font-style-row) row-type)}
                  color-style)
       data-heading]]
     [:tr
      [:td (merge {:class (h/<class style/td-stacked)}
                  color-style)
       data-value]]]))

(defn dynamic-table
  [{:keys [parsed-data even-row-background-color odd-row-background-color sub-heading-color
           sub-total-color total-color empty-line-color stacked-rows? text-color]
    :as   props}]
  (let [{:keys [headers
                heading-count _table-style rows _column-style-row icon-config-table]}
        parsed-data
        last-heading-idx (atom 0)]
    (if-not rows
      [:div (loc/tr :lp-report-template-incorrect-table-data)]
      [:div {:class (h/<class style/scroll-container)}
       [:table {:class (h/<class style/dynamic-table props)}
        (when (pos? heading-count)
          (into [:thead]
                (->> (range heading-count)
                     (mapv (fn [idx]
                             (let [hr (nth headers idx)]
                               (case (:header-type hr)
                                 :header-type/spanned
                                 (heading-row-spanned (nth headers idx))
                                 :header-type/info
                                 (heading-row-info (nth headers idx))
                                 :header-type/heading
                                 (heading-row-heading (nth headers idx))
                                 (str "No header type " (:header-type hr))
                               )))))))
        (when (pos? (count rows))
          (into [:tbody]
                (->> rows
                     (map-indexed (fn [idx row]
                                    (let [row-type (:row-type row)
                                          icons-row (when icon-config-table
                                                      (nth (->> icon-config-table
                                                                (drop heading-count))
                                                           idx))
                                          row-idx (if (some #(= row-type %)
                                                            ["subheading" "subtotal" "empty line" "total"])
                                                    (do (reset! last-heading-idx idx) 0)
                                                    (- idx @last-heading-idx))]
                                      (if stacked-rows?
                                        (data-row-stacked
                                         parsed-data
                                         row
                                         text-color)
                                        (data-row parsed-data
                                                  row
                                                  row-idx
                                                  even-row-background-color
                                                  odd-row-background-color
                                                  sub-heading-color
                                                  sub-total-color
                                                  total-color
                                                  empty-line-color
                                                  icons-row
                                                  props))))))))]])))


(defn table
  [name
   {:keys [_rich-content _rich-content-path figures] :as data}
   {:keys [_edit-mode? _save-text] :as props}]
  (let [removed-since-empty? (->> data
                                  :figures
                                  :rows
                                  (filter (fn [{:keys [row-data]}]
                                            (seq (remove nil? row-data))))
                                  (count)
                                  (= 0))]
    (if (and (not (:edit-mode? props))
             removed-since-empty?)
      nil
      [card/expandable-card
       (merge props 
              {:title (if removed-since-empty?
                        (str (if (cu/blank-value? name)
                               (->> figures
                                    :headers
                                    (first)
                                    :cols
                                    (first))
                               name)
                             "No data provided (table will be hidden)")
                        name)
               :removed-since-empty? removed-since-empty?})
       [:<>
        [dynamic-table
         {:parsed-data              (:figures data)
          :odd-row-background-color (or (:odd-row-background-color props)
                                        (theme/color [:lp-report :table :odd-row :bg]))
          :even-row-background-color (or (:even-row-background-color props)
                                         (theme/color [:lp-report :table :even-row :bg]))
          :sub-heading-color        (theme/color [:lp-report :table :subheading-color :bg])
          :sub-total-color          (theme/color [:lp-report :table :subtotal-color :bg])
          :total-color              (theme/color [:lp-report :table :total-color :bg])
          :empty-line-color         (theme/color [:lp-report :table :empty-line-color :bg])
          :fixed-width?             (:fixed-width? props)
          :stacked-rows?            (:stacked-rows? props)
          :text-color               (:text-color props)}]
        (when-not (or (:hide-footnote? props) removed-since-empty?) (text-content/optional-footnote data props))]])))
